import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { checkSmsChallenge, confirmSmsReset } from "@/lib/api";
import { HttpError } from "@/lib/http";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

const SOFT_MIN = 10; // backend enforces the authoritative role-aware policy

type Phase = "checking" | "valid" | "invalid" | "expired" | "used" | "locked" | "done";

function reasonToPhase(reason: string): Phase {
  if (reason === "expired") return "expired";
  if (reason === "used") return "used";
  if (reason === "too_many_attempts") return "locked";
  return "invalid";
}

/** Read the opaque challenge reference from the URL fragment (never sent to the
 *  server) and wipe it from the address bar immediately. */
function takeChallengeFromUrl(): string {
  let challenge = "";
  const m = /(?:^#|&)challenge=([^&]+)/.exec(window.location.hash || "");
  if (m) challenge = decodeURIComponent(m[1]);
  if (challenge) window.history.replaceState(null, "", window.location.pathname);
  return challenge;
}

export function ResetPasswordSmsPage() {
  const t = useT();
  const nav = useNavigate();
  const challengeRef = React.useRef<string | null>(null);
  if (challengeRef.current === null) challengeRef.current = takeChallengeFromUrl();
  const challenge = challengeRef.current;

  const [phase, setPhase] = React.useState<Phase>("checking");
  const [code, setCode] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    if (!challenge) { setPhase("invalid"); return; }
    checkSmsChallenge(challenge)
      .then((r) => { if (!cancelled) setPhase(r.valid ? "valid" : reasonToPhase(r.reason)); })
      .catch(() => { if (!cancelled) setPhase("invalid"); });
    return () => { cancelled = true; };
  }, [challenge]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pw !== confirm) { toast.error(t("operator.auth.password_mismatch")); return; }
    setSubmitting(true);
    try {
      await confirmSmsReset(challenge, code, pw);
      setPhase("done");
      toast.success(t("operator.auth.reset_done_toast"));
      setTimeout(() => nav("/login", { replace: true }), 1200);
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as any;
        if (body?.error === "weak_password") {
          toast.error(t("operator.auth.reset_weak_password"));
        } else if (body?.error === "invalid_code") {
          toast.error(t("operator.auth.reset_code_incorrect"));
        } else if (body?.error === "too_many_attempts") {
          setPhase("locked");
        } else if (body?.error === "invalid_challenge") {
          setPhase(reasonToPhase(body.reason || "invalid"));
        } else {
          toast.error(errorMessage(t, err, { fallback: "operator.auth.reset_failed" }));
        }
      } else {
        toast.error(errorMessage(t, err, { fallback: "operator.auth.reset_failed" }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = code.trim().length === 6 && pw.length >= SOFT_MIN && pw === confirm && !submitting;

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{t("operator.auth.reset_title")}</CardTitle>
            <CardDescription>
              {phase === "valid"
                ? t("operator.auth.reset_sms_description")
                : t("operator.auth.reset_subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {phase === "checking" && <div className="text-sm opacity-70">{t("operator.auth.reset_checking")}</div>}

            {(phase === "invalid" || phase === "expired" || phase === "used" || phase === "locked") && (
              <div className="space-y-4">
                <div className="text-sm text-red-600 dark:text-red-400">
                  {t("operator.auth.reset_sms_state", { state: phase })}
                </div>
                <div className="text-xs opacity-60">
                  {t("operator.auth.reset_sms_retry_hint")}
                </div>
                <Button className="w-full" variant="outline" onClick={() => nav("/login")}>
                  {t("operator.auth.back_to_sign_in")}
                </Button>
              </div>
            )}

            {phase === "done" && (
              <div className="space-y-4">
                <div className="text-sm text-green-600 dark:text-green-400">{t("operator.auth.reset_done")}</div>
                <Button className="w-full" onClick={() => nav("/login", { replace: true })}>{t("operator.auth.sign_in")}</Button>
              </div>
            )}

            {phase === "valid" && (
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label>{t("operator.auth.verification_code")}</Label>
                  <Input
                    className="mt-2 tracking-widest"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder={t("operator.auth.verification_code_placeholder")}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                  />
                </div>
                <div>
                  <Label>{t("operator.auth.new_password")}</Label>
                  <div className="relative mt-2">
                    <Input
                      className="pr-10"
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                    />
                    <button type="button" tabIndex={-1}
                      aria-label={show ? t("operator.auth.hide_password") : t("operator.auth.show_password")}
                      onClick={() => setShow((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="mt-1 text-xs opacity-60">{t("operator.auth.password_length", { length: pw.length, min: SOFT_MIN })}</div>
                </div>
                <div>
                  <Label>{t("operator.auth.confirm_password")}</Label>
                  <Input className="mt-2" type={show ? "text" : "password"} autoComplete="new-password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                  {confirm.length > 0 && pw !== confirm && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400">{t("operator.auth.password_mismatch")}</div>
                  )}
                </div>
                <Button className="w-full" type="submit" disabled={!canSubmit}>
                  {submitting ? t("common.state.saving") : t("operator.auth.set_new_password")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
